<template>
    <div class="pdp-image-gallery">
        <div class="desktop-gallery-wrapper">
            <Teleport v-if="documentReady" to="body">
                <a @click.prevent="scrollToEndOfImages()" v-if="showSeeLessOverlayButton" class="see-less-overlay" aria-label="hidden" style="z-index:999">
                    <button id="showMoreButton" class="secondary btn-less" @click="toggleShowMore()">
                        See Less
                        <div aria-label="hidden">
                            <WhitePlusIcon alt="plus-icon" />
                        </div>
                    </button>
                </a>
            </Teleport>
            <div ref="imageGallery" class="gallery">
                 <div v-if="showBestSeller" class="chip-wrapper">
                        <p>Best Seller</p>
                    </div>
                <div class="item main zoom-image-container">
                    <a v-if="!useComingSoonImage" href="#" aria-label="zoom">
                        <ZoomHoverIcon class="zoom-icon" alt="zoom-icon"/>
                    </a>
                    <span class="action-button">
                        <slot></slot>
                    </span>
                   
                    <Media
                        v-if="!useComingSoonImage"
                        v-bind="{ ...primaryMedia, zoomControls }"
                        mediaClasses="w-image main-image" alt=""
                    />
                    <Media
                        v-else
                        v-bind="{ media: comingSoonImages().large }"
                        mediaClasses="w-image main-image coming-soon-image" alt=""
                    />
                </div>
                <!-- removed class "small" from div below, was conflicting with zoomable component -->
                <div v-for="(media, index) in secondaryMedia" :key="`${media.media ?? ''}-${index}`" :id="`gallery_media_container_${index}`"
                    :class="{'item': true, 'zoom-image-container': media.defaultMediaType != 'iframe', 'hidden': !showingMore && index > 5, 'video': media.defaultMediaType == 'iframe'}">
                    <Media
                        v-if="media.defaultMediaType === 'image'"
                        :id="`gallery_media_${index}`"
                        v-bind="{ ...media, zoomControls }"
                        mediaClasses="image"
                        alt=""
                    />
                    <iframe class="iframe-media" v-else-if="media.defaultMediaType == 'iframe'" :src="media.mediaUrl"></iframe>
                </div>
            </div>
            <a @click.prevent="scrollToEndOfImages()" ref="seeMoreButton" class="see-more" aria-label="hidden">
                <button id="showMoreButton" class="secondary btn-more" v-if="secondaryMedia.length > 6" @click="toggleShowMore()">
                    See {{ showingMore ? 'Less' : 'More' }}
                    <div aria-label="hidden">
                        <WhitePlusIcon alt="plus-icon" />
                    </div>
                </button>
            </a>
            <a v-if="is_dealer && !useComingSoonImage && !isDownloadingImages" @click.prevent="downloadImages" href="#" class="download-link" aria-label="hidden">
                <DownloadIcon alt="download-icon" />
                <p class="title-xs">Download Photos</p>
            </a>
            <div v-else-if="is_dealer && isDownloadingImages" class="download-link downloading" aria-label="hidden">
                <LoadingIcon alt="loading-icon" />
                <p class="title-xs">
                    Downloading Photos
                </p>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import Media from "@/components/Media.vue";
    import { type MediaProps } from "@interfaces/MediaProps";
    import { type ZoomControls } from "@interfaces/ZoomControls";
    import { ref, reactive, computed, watch, onMounted, nextTick, type PropType } from "vue";
    import { storeToRefs } from "pinia";
    import { useSettingsStore } from "@/store/settings";
    import { useElementVisibility } from "@vueuse/core";
    import WhitePlusIcon from "@icons/white-plus.svg";
    import ZoomHoverIcon from "@icons/zoom-hover.svg";
    import DownloadIcon from "@icons/download.svg";
    import LoadingIcon from "@icons/loading.svg";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"

    let props = defineProps({
        productImages: {
            type: Array as PropType<MediaProps[]>,
            required: true
        },
        downloadImages: {
            type: Function,
            required: false,
            default(){
                return ()=>{}
            }
        },
        isDownloadingImages: {
            type: Boolean,
            required: false,
            default: false
        },
        videoFile: {
            type: String,
            required: false,
            default: ""
        },
        showBestSeller: {
            type: Boolean,
            required: false,
            default: false
        }
    });

    const primaryMedia = computed(() => {
        if(props.productImages.length < 1){
            return {};
        }
        return props.productImages[0] ?? {};
    });

    const useComingSoonImage = computed(()=>{
        return props.productImages.length == 0;
    })
    
    const secondaryMedia = computed(() => {
        let videoFile;
        try {
            const originalUrl = new URL(props.videoFile);
            let host = originalUrl.hostname.replace(/^www\./, "");
            let videoId = originalUrl.searchParams.get("v");
            let startTimeValue = originalUrl.searchParams.get("t")
                ?? originalUrl.hash.match(/^#t=(.+)$/)?.[1]
                ?? null;
            let newUrl;
            if(host === "youtube.com"){
                if(originalUrl.pathname.startsWith("/embed/")){
                    newUrl = new URL(originalUrl);
                    newUrl.hostname = 'youtube.com';
                }else if(videoId){
                    newUrl = new URL(originalUrl);
                    newUrl.hostname = 'youtube.com';
                    newUrl.pathname = `/embed/${videoId}`;
                    newUrl.searchParams.delete("v");
                }
            }else if(host === "youtu.be"){
                newUrl = new URL(originalUrl);
                newUrl.hostname = 'youtube.com';
                newUrl.pathname = `/embed${newUrl.pathname}`;
            }
            if(newUrl){
                if(startTimeValue){
                    const [, hours = 0, minutes = 0, seconds = 0] =
                        startTimeValue.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/) || [];
                    const totalSeconds = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
                    newUrl.searchParams.set("start", String(totalSeconds || startTimeValue));
                    newUrl.searchParams.delete("t");
                    newUrl.hash = "";
                }
                videoFile = newUrl.toString();
            }
        } catch {
            // invalid URL
            videoFile = null;
        }
        
        if(props.productImages.length < 2){
            if(!videoFile){
                return [];
            }

            return [{
                defaultMediaType: "iframe",
                mediaUrl: videoFile
            }];
        }
        let productImages = props.productImages.slice(1) ?? [];
        //add video at index 1 of the productImages array
        if(videoFile){
            let firstImage = productImages.shift();

            productImages.unshift({
                defaultMediaType: "iframe",
                mediaUrl: videoFile
            });
            productImages.unshift(firstImage);
        }
        
        return productImages;
    });

    let showingMore = ref(false);
    function toggleShowMore() {
        showingMore.value = !showingMore.value;
    }

    const seeMoreButton = ref(null);
    let seeMoreButtonIsVisible = ref(false)
    let imageGalleryIsVisible = ref(false)

    
    const imageGallery = ref(null);
    onMounted(()=>{
        let seeMoreButtonCheck = useElementVisibility(seeMoreButton);
        let imageGalleryCheck = useElementVisibility(imageGallery);

        watch(seeMoreButtonCheck, (value)=>{
            seeMoreButtonIsVisible.value = value;
        })

        watch(imageGalleryCheck, (value)=>{
            imageGalleryIsVisible.value = value;
        })
    } )  

    const showSeeLessOverlayButton = computed(()=>{
        return secondaryMedia.value.length > 6 && showingMore.value == true && !seeMoreButtonIsVisible.value && imageGalleryIsVisible.value;
    })

    const scrollToEndOfImages = ()=>{
        nextTick(()=>{
            if(!showingMore.value){
                seeMoreButton.value?.scrollIntoView({behavior: "auto", block: "center", inline: "nearest"});
            }
        })
    }

    let zoomControls = {
        allow: true,
        moveType: "pan",
        zoomType: "click",
        zoomPreload: true,
        hideCloseButton: true
    };

    let settings = useSettingsStore();
    const { is_dealer } = storeToRefs(settings);

    const downloadPhotos = ()=>{
        console.log("downloading photos")
        // TODO : need backend for downloading photos
    }

    const documentReady = ref(false);
    onMounted(() => {
        documentReady.value = !!document.body;
    });
</script>
